import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkImportConfigDto,
  CreateBulkImportDto,
  CreateBulkImportResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

const IMPORT_REMOVAL_WINDOW_MS = 30 * 60 * 1000;

const UNLOAD_QUERY_KEYS: Record<'IDEAS' | 'FEEDBACKS' | 'CUSTOMERS', readonly QueryKey[]> = {
  IDEAS: [['/ideas'], ['bulk-import', 'latest', 'ideas'], ['bulk-import']],
  FEEDBACKS: [
    ['/feedback', 'GetAllQuery'],
    ['bulk-import', 'latest', 'feedbacks'],
    ['bulk-import'],
  ],
  CUSTOMERS: [['customers'], ['bulk-import', 'latest', 'customers'], ['bulk-import']],
};

export const useBulkImportApi = (api: AxiosInstance) => {
  const baseUrl = `bulk-import`;

  const bulkImport = (url: 'customers' | 'feedbacks' | 'ideas') => {
    return async (payload: CreateBulkImportDto): Promise<CreateBulkImportResponseDto> => {
      const { data } = await api.post(`${baseUrl}/${url}`, payload);
      return data;
    };
  };
  const unloadImportJob = async (
    importId: number,
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    const { data } = await api.delete(`${baseUrl}/${importId}`);
    return data;
  };

  const getLatestImportJob = async (
    importType: 'CUSTOMERS' | 'IDEAS' | 'FEEDBACKS',
  ): Promise<{
    deletedAt: boolean;
    canUnload: boolean;
    timeRemaining: number;
    createdAt: string;
    id: number;
    userId: string;
    config: BulkImportConfigDto;
    owner: string;
  }> => {
    let { data } = await api.get(`${baseUrl}/latest/${importType}`);
    if (!data) {
      data = {
        canUnload: false,
        timeRemaining: 0,
        createdAt: '',
        id: 0,
        userId: '',
        config: {},
        owner: '',
      };
      return data;
    }
    if (data.deletedAt) {
      data.canUnload = false;
    } else {
      const canUnload = new Date(data.createdAt).getTime() + IMPORT_REMOVAL_WINDOW_MS > Date.now();
      data.canUnload = canUnload;
    }

    const timeRemaining = Math.floor(
      (IMPORT_REMOVAL_WINDOW_MS - (Date.now() - new Date(data.createdAt).getTime())) / (60 * 1000),
    );
    data.timeRemaining = Math.max(0, timeRemaining);

    return data;
  };

  const queryClient = useQueryClient();

  const useBulkImport = (url: 'customers' | 'feedbacks' | 'ideas') =>
    useErrorHandlingMutation({
      mutationFn: bulkImport(url),
      onSuccess: () => {
        if (url === 'ideas') {
          queryClient.invalidateQueries({ queryKey: ['/ideas'] });
          queryClient.invalidateQueries({ queryKey: ['bulk-import', 'latest', 'ideas'] });
        } else if (url === 'feedbacks') {
          queryClient.invalidateQueries({ queryKey: ['/feedback'] });
          queryClient.invalidateQueries({ queryKey: ['bulk-import', 'latest', 'feedbacks'] });
        } else {
          queryClient.invalidateQueries({ queryKey: ['customers'] });
          queryClient.invalidateQueries({ queryKey: ['bulk-import', 'latest', 'customers'] });
        }
      },
    });

  const useGetLatestImportJob = (importType: 'CUSTOMERS' | 'IDEAS' | 'FEEDBACKS') =>
    useQuery({
      queryKey: ['bulk-import', 'latest', importType.toLowerCase()],
      queryFn: () => getLatestImportJob(importType),
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    });

  const useUnloadImportJob = (
    importType: 'CUSTOMERS' | 'IDEAS' | 'FEEDBACKS',
    mutationOnSuccess: () => void,
    mutationOnError: () => void,
  ) =>
    useErrorHandlingMutation({
      mutationFn: unloadImportJob,
      onSuccess: async () => {
        await Promise.all(
          UNLOAD_QUERY_KEYS[importType].map((queryKey) =>
            queryClient.invalidateQueries({ queryKey }),
          ),
        );
        mutationOnSuccess();
      },
      onError: () => {
        mutationOnError();
      },
    });

  return { useBulkImport, useGetLatestImportJob, useUnloadImportJob };
};
